import { AlertCircle, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useId, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import type { CountState } from "../api/useSegmentCounts";
import {
  LOGIC,
  SEGMENT_LIMITS,
  SEGMENT_TRANSITION,
  type Logic,
} from "../constants";
import { getLogicLabel, getRuleLabel } from "../labels";
import type { FormRule } from "../schemas";
import { ConditionDetails } from "./ConditionDetails";
import { ConditionListItem } from "./ConditionListItem";
import { LimitAddButton } from "./LimitAddButton";
import { LogicToggle } from "./LogicToggle";
import { VacancyCountBadge } from "./VacancyCountBadge";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import { Card } from "@/components/ui/v2/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import type {
  ConditionOverlap,
  RuleOverlap,
  SegmentField,
} from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { cn } from "@/lib/utils/cn";

export type RuleSnapshot = {
  rule: FormRule;
  invalidConditions: boolean[];
  conditionsMessage?: string;
  matchMessage?: string;
};

type RuleBlockViewProps = RuleSnapshot & {
  ruleIndex: number;
  readOnly: boolean;
  exiting?: boolean;
  fields: SegmentField[];
  count: CountState;
  activeConditionKey: string | null;
  overlap?: RuleOverlap;
  badgeListRef?: RefObject<HTMLUListElement>;
  renderEditor: (conditionIndex: number) => ReactNode;
  onMatchChange: (type: Logic) => void;
  onAddCondition: () => void;
  onRemoveCondition: (conditionIndex: number) => void;
  onOpenChange: (clientKey: string, open: boolean) => void;
  onRemove: () => void;
  editedHint?: string;
};

const INVALID_CONTROL_SELECTOR = '[aria-invalid="true"]';

const handleOpenAutoFocus = (event: Event) => {
  if (!(event.currentTarget instanceof HTMLElement)) return;
  const invalidControl = event.currentTarget.querySelector<HTMLElement>(
    INVALID_CONTROL_SELECTOR,
  );
  if (!invalidControl) return;
  event.preventDefault();
  invalidControl.focus();
};

export function RuleBlockView({
  ruleIndex,
  rule,
  invalidConditions,
  conditionsMessage,
  matchMessage,
  readOnly,
  exiting = false,
  fields,
  count,
  activeConditionKey,
  overlap,
  badgeListRef,
  renderEditor,
  onMatchChange,
  onAddCondition,
  onRemoveCondition,
  onOpenChange,
  onRemove,
  editedHint,
}: RuleBlockViewProps) {
  const { t } = useTranslation();
  const matchMessageId = useId();
  const conditions = rule.conditions;
  const logicLabel = rule.type && getLogicLabel(t, rule.type);
  const toConditionOverlap = (
    conditionKey: string,
  ): ConditionOverlap | undefined => {
    const mark = overlap?.byCondition.get(conditionKey);
    if (!overlap || !mark) return undefined;
    return {
      field: mark.field,
      isHighlighted: mark.groupKey === overlap.highlightedGroupKey,
      onHighlightChange: (isHighlighted) =>
        overlap.onHighlight(isHighlighted ? mark.groupKey : null),
    };
  };
  const isTourRule = ruleIndex === 0 && !exiting;
  const matchRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.MATCH,
    isTourRule,
  );
  const conditionsRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.CONDITIONS,
    isTourRule,
  );
  const countRef = useTourTarget<HTMLSpanElement>(
    SEGMENT_TOUR_TARGET.COUNT,
    isTourRule,
  );

  return (
    <Card
      role="group"
      aria-label={getRuleLabel(t, ruleIndex)}
      data-testid="segment-rule"
      aria-hidden={exiting || undefined}
      className={cn(
        "items-start gap-0 self-stretch overflow-hidden p-0",
        exiting && "pointer-events-none",
      )}
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-3 border-b bg-card px-4 py-3 md:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm">{getRuleLabel(t, ruleIndex)}</span>
          <span aria-hidden className="h-4 w-px bg-border" />
          <span className="text-sm text-muted-foreground">
            {t("admin.segments.builder.rule.match")}
          </span>
          <LogicToggle
            rootRef={matchRef}
            value={rule.type}
            onChange={onMatchChange}
            label={t("admin.segments.builder.rule.match")}
            readOnly={readOnly || exiting}
            aria-invalid={!!matchMessage}
            aria-describedby={matchMessage ? matchMessageId : undefined}
          />
          {rule.type && (
            <span className="text-sm text-muted-foreground">
              {rule.type === LOGIC.AND
                ? t("admin.segments.builder.rule.matchAll")
                : t("admin.segments.builder.rule.matchAny")}
            </span>
          )}
          {matchMessage && (
            <span
              id={matchMessageId}
              role="alert"
              className="text-sm text-destructive"
            >
              {matchMessage}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <VacancyCountBadge
            {...count}
            testId="rule-count"
            badgeRef={countRef}
          />
          {!readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost-destructive"
                  size="icon"
                  onClick={onRemove}
                  aria-label={t("common.removeRule")}
                >
                  <Trash2 className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("common.removeRule")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div
        ref={conditionsRef}
        className="flex w-full flex-col gap-4 p-4 md:p-6"
      >
        {conditions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t("admin.segments.builder.rule.empty")}
          </p>
        )}
        <div className="flex flex-col gap-2">
          <ul
            ref={badgeListRef}
            className="flex flex-wrap items-center gap-x-2 gap-y-2"
          >
            <AnimatePresence initial={false}>
              {conditions.map((condition, conditionIndex) => (
                <ConditionListItem
                  key={condition.clientKey}
                  condition={condition}
                  fields={fields}
                  hasError={invalidConditions[conditionIndex] ?? false}
                  isOpen={condition.clientKey === activeConditionKey}
                  readOnly={readOnly}
                  overlap={toConditionOverlap(condition.clientKey)}
                  logicLabel={
                    conditionIndex < conditions.length - 1
                      ? logicLabel
                      : undefined
                  }
                  onOpenChange={(open) => onOpenChange(condition.clientKey, open)}
                  onOpenAutoFocus={handleOpenAutoFocus}
                  onRemove={() => onRemoveCondition(conditionIndex)}
                >
                  {readOnly ? (
                    <ConditionDetails condition={condition} fields={fields} />
                  ) : (
                    renderEditor(conditionIndex)
                  )}
                </ConditionListItem>
              ))}
            </AnimatePresence>
            {!readOnly && (
              <motion.li
                layout="position"
                transition={SEGMENT_TRANSITION}
                className="inline-flex"
                data-add-condition
              >
                <LimitAddButton
                  label={t("admin.segments.builder.addCondition")}
                  limitMessage={t("admin.segments.builder.limit.conditions", {
                    count: SEGMENT_LIMITS.conditions,
                  })}
                  isAtLimit={conditions.length >= SEGMENT_LIMITS.conditions}
                  onAdd={onAddCondition}
                />
              </motion.li>
            )}
          </ul>
          {!readOnly && editedHint && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="rule-edited-hint"
            >
              {editedHint}
            </p>
          )}
        </div>

        {conditionsMessage && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertDescription>{conditionsMessage}</AlertDescription>
          </Alert>
        )}
      </div>
    </Card>
  );
}
